import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircularProgress from "@mui/material/CircularProgress";
import ReactMarkdown from "react-markdown";

import {
  API_get_file_upload_text,
  API_get_file_upload,
} from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";

/**
 * In-app preview for markdown / plain-text documents. Clicking an
 * imported page opens this instead of downloading a .md file the OS
 * doesn't know what to do with. Renders via react-markdown (already a
 * dependency — the assistant chat uses it); a Download action keeps the
 * old behavior reachable.
 */
export default function MarkdownPreviewModal({ open, onClose, companyId, file }) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [text, setText] = React.useState("");
  const [truncated, setTruncated] = React.useState(false);
  const [sourceUrl, setSourceUrl] = React.useState(null);

  React.useEffect(() => {
    if (!open || !file?.uuid) return;
    let cancelled = false;
    async function load() {
      setText("");
      setTruncated(false);
      setSourceUrl(null);
      setLoading(true);
      setError(false);
      try {
        const res = await getRightworkAPI(API_get_file_upload_text, {
          company_id: companyId,
          file_upload_uuid: file.uuid,
        });
        if (cancelled) return;
        const payload = res?.data?.redux?.payload;
        setText(payload?.text || "");
        setTruncated(Boolean(payload?.truncated));
        setSourceUrl(payload?.source_url || null);
      } catch (err) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, file?.uuid]);

  const handleDownload = async () => {
    try {
      const res = await getRightworkAPI(API_get_file_upload, {
        file_upload_uuid: file?.uuid,
      });
      const url = res?.data?.redux?.payload?.presigned_url;
      if (url) window.open(url, "_blank");
    } catch (err) {
      // Non-fatal — the preview is still on screen.
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="md-preview-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(760px, 94vw)",
          maxHeight: "min(86vh, 820px)",
          bgcolor: "background.paper",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 gap-3">
          <div className="min-w-0">
            <h2
              id="md-preview-title"
              className="text-base font-semibold text-slate-900 truncate"
            >
              {file?.displayName || file?.name}
            </h2>
            {sourceUrl && (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-violet-700 hover:underline truncate block"
              >
                {sourceUrl}
              </a>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={handleDownload}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
              aria-label="Download"
              title="Download"
            >
              <OpenInNewIcon style={{ fontSize: 18 }} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
              aria-label="Close"
            >
              <CloseIcon style={{ fontSize: 20 }} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="py-12 text-center">
              <CircularProgress size={24} sx={{ color: "#6d28d9" }} />
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-rose-600">
              Couldn't load this document. Close and reopen to retry.
            </div>
          ) : (
            <>
              {/* prose styling keeps headings/lists readable without a
                  bespoke stylesheet; matches assistant-chat markdown. */}
              <div className="prose prose-sm prose-slate max-w-none">
                <ReactMarkdown>{text}</ReactMarkdown>
              </div>
              {truncated && (
                <p className="mt-4 text-xs text-slate-400">
                  Preview truncated — download the file for the full
                  content.
                </p>
              )}
            </>
          )}
        </div>
      </Box>
    </Modal>
  );
}
